import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
// PART C (C6) — real idleness, shared by every tab of this browser. Activity in one tab keeps the
// others alive, and a logout (or the timer firing) signs every tab out at once.
// PART C FIX-UP (Fix 6c): the limit comes from configuration (security / idle_timeout_minutes),
// so an Admin can change it in System Settings; 240 minutes when the row is missing.
const DEFAULT_IDLE_MINUTES = 240;
const ACTIVITY_KEY = "orbit_last_activity";
const LOGOUT_KEY = "orbit_logged_out_at";
const LOGOUT_REASON_KEY = "orbit_logout_reason";
const IDLE_MINUTES_KEY = "orbit_idle_minutes";
const TICK_MS = 15 * 1000;

const readLastActivity = () => {
  const v = Number(window.localStorage.getItem(ACTIVITY_KEY) || 0);
  return Number.isFinite(v) && v > 0 ? v : 0;
};

const readIdleMinutes = () => {
  const v = Number(window.localStorage.getItem(IDLE_MINUTES_KEY) || 0);
  return Number.isFinite(v) && v > 0 ? v : DEFAULT_IDLE_MINUTES;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null = checking, false = not auth'd
  const [loading, setLoading] = useState(true);
  // why the last sign-out happened, so the login page can say it in plain words
  const [logoutReason, setLogoutReason] = useState(() => {
    try { return window.localStorage.getItem(LOGOUT_REASON_KEY) || ""; } catch { return ""; }
  });
  const [idleMinutes, setIdleMinutes] = useState(readIdleMinutes);
  const tickRef = useRef(null);
  const idleRef = useRef(readIdleMinutes());

  const performLogout = useCallback(async (reason = "manual", broadcast = true) => {
    try {
      window.localStorage.setItem(LOGOUT_REASON_KEY, reason);
      window.localStorage.setItem(IDLE_MINUTES_KEY, String(idleRef.current));
      if (broadcast) window.localStorage.setItem(LOGOUT_KEY, String(Date.now()));
    } catch { /* private mode */ }
    setLogoutReason(reason);
    try { await api.post("/auth/logout", { reason }); } catch { /* cookie is cleared anyway */ }
    setUser(false);
  }, []);

  const markActivity = useCallback(() => {
    try { window.localStorage.setItem(ACTIVITY_KEY, String(Date.now())); } catch { /* ignore */ }
  }, []);

  // The idle limit is read once the user is in; the server is the only source of it.
  useEffect(() => {
    if (!user) return;
    api.get("/config", { params: { group: "security" } })
      .then(({ data }) => {
        const row = (data || []).find(i => i.config_key === "idle_timeout_minutes");
        const mins = Number(row?.config_value);
        const value = Number.isFinite(mins) && mins > 0 ? mins : DEFAULT_IDLE_MINUTES;
        idleRef.current = value;
        setIdleMinutes(value);
        try { window.localStorage.setItem(IDLE_MINUTES_KEY, String(value)); } catch { /* ignore */ }
      })
      .catch(() => { idleRef.current = DEFAULT_IDLE_MINUTES; });
  }, [user]);

  // One timer per tab, but the deadline is the SHARED last-activity stamp, so three open tabs
  // cannot each hold their own clock and keep the session alive forever.
  useEffect(() => {
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    const handler = () => markActivity();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    if (!readLastActivity()) markActivity();

    tickRef.current = setInterval(() => {
      const last = readLastActivity();
      if (last && Date.now() - last >= idleRef.current * 60 * 1000) {
        performLogout("idle_timeout");
      }
    }, TICK_MS);

    const onStorage = (e) => {
      if (e.key === LOGOUT_KEY && e.newValue) {
        // another tab signed out — follow it and tell the server why, so the audit shows both
        setLogoutReason(window.localStorage.getItem(LOGOUT_REASON_KEY) || "other_tab");
        api.post("/auth/logout", { reason: "other_tab" }).catch(() => {});
        setUser(false);
      }
      if (e.key === IDLE_MINUTES_KEY && e.newValue) {
        const mins = Number(e.newValue);
        if (Number.isFinite(mins) && mins > 0) { idleRef.current = mins; setIdleMinutes(mins); }
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      window.removeEventListener("storage", onStorage);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [markActivity, performLogout]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    setLogoutReason("");
    try {
      window.localStorage.removeItem(LOGOUT_KEY);
      window.localStorage.removeItem(LOGOUT_REASON_KEY);
    } catch { /* ignore */ }
    markActivity();
    return data;
  };

  const logout = async () => {
    await performLogout("manual");
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  };

  const resetPassword = async (token, newPassword) => {
    const { data } = await api.post("/auth/reset-password", { token, new_password: newPassword });
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, forgotPassword, resetPassword,
      formatApiError, logoutReason, idleMinutes }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
}
